import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface TechnologyDTO {
    id: number;
    name: string;
    icon?: string;
}

export default function TechnologyManager() {
    const [technologies, setTechnologies] = useState<TechnologyDTO[]>([]);
    const [name, setName]                 = useState('');
    const [icon, setIcon]                 = useState('');
    const [confirmId, setConfirmId]       = useState<number | null>(null);

    const fetchTechnologies = async () => {
        try {
            const res = await axios.get<TechnologyDTO[]>('/api/technologies');
            setTechnologies(res.data);
        } catch (err) {
            console.error('Failed to load technologies:', err);
        }
    };

    const addTechnology = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;
        try {
            const res = await axios.post<TechnologyDTO>('/api/technologies', { name, icon });
            setTechnologies(prev => [...prev, res.data]);
            setName('');
            setIcon('');
        } catch (err) {
            console.error('Failed to create technology:', err);
        }
    };

    const deleteTechnology = async (id: number) => {
        try {
            await axios.delete(`/api/technologies/${id}`);
            setTechnologies(prev => prev.filter(t => t.id !== id));
            setConfirmId(null);
        } catch (err) {
            console.error('Failed to delete technology:', err);
        }
    };

    useEffect(() => { fetchTechnologies(); }, []);

    return (
        <div className="max-w-2xl">
            {/* Add form */}
            <form onSubmit={addTechnology} className="mb-6 bg-surface p-5 rounded border border-border space-y-4">
                <h2 className="text-base font-semibold text-on-surface">Add Technology</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Name *</label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="e.g. Spring Boot"
                            className="w-full bg-background border border-border rounded px-3 py-2 text-on-surface text-sm"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Icon URL (optional)</label>
                        <input
                            type="text"
                            value={icon}
                            onChange={e => setIcon(e.target.value)}
                            placeholder="https://… or spring.svg"
                            className="w-full bg-background border border-border rounded px-3 py-2 text-on-surface text-sm"
                        />
                    </div>
                </div>
                <button
                    type="submit"
                    className="btn-primary"
                >
                    Add
                </button>
            </form>

            {/* Technology list */}
            <div className="bg-surface rounded border border-border overflow-hidden">
                <table className="min-w-full text-sm">
                    <thead className="bg-paper font-label text-sm text-muted">
                    <tr>
                        <th className="px-4 py-3 text-left">Icon</th>
                        <th className="px-4 py-3 text-left">Name</th>
                        <th className="px-4 py-3 text-left">Actions</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                    {technologies.length === 0 && (
                        <tr>
                            <td colSpan={3} className="px-4 py-6 text-center text-text-secondary">
                                No technologies yet.
                            </td>
                        </tr>
                    )}
                    {technologies.map(tech => (
                        <tr key={tech.id} className="hover:bg-background transition-colors">
                            <td className="px-4 py-3">
                                {tech.icon
                                    ? <img src={tech.icon} alt="" className="w-7 h-7 object-contain" />
                                    : <span className="text-text-secondary">—</span>
                                }
                            </td>
                            <td className="px-4 py-3 font-medium text-on-surface">{tech.name}</td>
                            <td className="px-4 py-3">
                                <button
                                    onClick={() => setConfirmId(tech.id)}
                                    className="btn-danger btn-sm"
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Delete confirmation */}
            {confirmId !== null && (
                <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50">
                    <div className="bg-surface border border-border rounded p-6 w-full max-w-sm shadow-md">
                        <h3 className="text-lg font-semibold text-on-surface mb-2">Delete Technology</h3>
                        <p className="text-text-secondary mb-6">
                            Are you sure you want to delete <strong className="text-on-surface">
                                {technologies.find(t => t.id === confirmId)?.name}
                            </strong>?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setConfirmId(null)}
                                className="px-4 py-2 rounded border border-border text-on-surface hover:bg-background transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => deleteTechnology(confirmId)}
                                className="btn-danger"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
